// 利用者の画面: 概要＋タブ（記録・帳票・エージェント）。タブは URL で切り替える。
import { useState } from 'react';
import { Link, NavLink, Outlet, useNavigate, useOutletContext, useParams } from 'react-router';
import { ArrowLeft, ChevronRight, Sparkles } from 'lucide-react';
import { FORMS, CAREMANAGER_FORMS } from '@alfine/shared/forms';
import { api, officePath } from '../lib/api';
import { CASE_KIND_LABEL, CASE_STATE_LABEL, DOC_STATUS_LABEL, canConfirm, canReview, formatDateTime, groupByDocType, outcomeLabel, parseAgentOutcome, questionText, recent, type AgentOutcome } from '../lib/domain';
import { messageOf } from '../lib/errors';
import { useLoad } from '../lib/useLoad';
import type { AgentInvokeResult, CareDocument, CareRecord, Client, DocStatus, Items, WorkCase } from '../lib/types';
import { CaseAnswerForm } from '../components/CaseAnswer';
import { DocumentBody } from '../components/DocumentBody';
import { NeedOffice } from '../components/Shell';
import { Badge, Button, Card, ErrorText, Loading, Muted, Textarea, cx } from '../components/ui';

const APP_CODE = 'caremanager';

interface ClientCtx {
  officeId: string;
  client: Client;
}

const useClientCtx = () => useOutletContext<ClientCtx>();

function Tab({ to, label }: { to: string; label: string }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) => cx('-mb-px border-b-2 px-3 py-2 text-[15px]', isActive ? 'border-accent font-medium text-accent' : 'border-transparent text-ink-3 hover:text-ink-1')}
    >
      {label}
    </NavLink>
  );
}

function ClientLayout({ officeId, clientId }: { officeId: string; clientId: string }) {
  // 単体取得の API が無いので、一覧から探す（API の宿題）
  const { data, error, loading } = useLoad(
    () => api<Items<Client>>(officePath(officeId, 'clients'), { officeId }).then((r) => r.items.find((c) => c.id === clientId) ?? null),
    [officeId, clientId],
  );
  if (loading) return <Loading />;
  if (error) return <ErrorText>{error}</ErrorText>;
  if (!data)
    return (
      <Card className="space-y-2">
        <p>この事業所に該当する利用者が見つかりませんでした。</p>
        <Link to="/clients" className="text-sm text-accent underline">
          利用者の一覧へ
        </Link>
      </Card>
    );
  const base = `/clients/${encodeURIComponent(clientId)}`;
  return (
    <div className="space-y-4">
      <Link to="/clients" className="inline-flex items-center gap-1 text-sm text-ink-3 hover:text-ink-1">
        <ArrowLeft size={14} />
        利用者
      </Link>
      <div className="flex flex-wrap items-baseline gap-3">
        <h1 className="text-xl font-semibold">{data.name}</h1>
        {data.kana ? <span className="text-sm text-ink-3">{data.kana}</span> : null}
        <Badge tone="accent">{data.careLevel ?? '要介護度 未設定'}</Badge>
        {data.birthDate ? <span className="text-sm text-ink-3">生年月日 {data.birthDate}</span> : null}
      </div>
      <nav className="flex gap-1 border-b border-line" aria-label="利用者のタブ">
        <Tab to={`${base}/records`} label="記録" />
        <Tab to={`${base}/documents`} label="帳票" />
        <Tab to={`${base}/agent`} label="エージェント" />
      </nav>
      <Outlet context={{ officeId, client: data } satisfies ClientCtx} />
    </div>
  );
}

export function ClientDetailPage() {
  const { clientId = '' } = useParams();
  return <NeedOffice>{(officeId) => <ClientLayout key={`${officeId}:${clientId}`} officeId={officeId} clientId={clientId} />}</NeedOffice>;
}

// ---- 記録 ----

export function RecordsTab() {
  const { officeId, client } = useClientCtx();
  const { data, error, loading, reload } = useLoad(
    () => api<Items<CareRecord>>(officePath(officeId, 'clients', client.id, 'records'), { officeId }).then((r) => r.items),
    [officeId, client.id],
  );
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const submit = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      await api(officePath(officeId, 'clients', client.id, 'records'), { officeId, body: { appCode: APP_CODE, kind: 'visit_note', body: { text: text.trim() } } });
      setText('');
      reload();
    } catch (e) {
      setSaveError(messageOf(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <form
          className="space-y-2"
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          <label className="block space-y-1">
            <span className="text-sm font-medium text-ink-2">訪問記録を残す</span>
            <Textarea data-testid="record-text" value={text} onChange={(e) => setText(e.target.value)} placeholder="訪問・面談で見聞きしたことを書いてください" disabled={saving} />
          </label>
          <ErrorText>{saveError}</ErrorText>
          <div className="flex justify-end">
            <Button data-testid="record-submit" type="submit" disabled={saving || !text.trim()}>
              {saving ? '保存中…' : '記録する'}
            </Button>
          </div>
        </form>
      </Card>
      <ErrorText>{error}</ErrorText>
      {loading && !data ? <Loading /> : null}
      {data && data.length === 0 ? <Muted>記録はまだありません。</Muted> : null}
      {data?.map((r) => (
        <Card key={r.id} data-testid="record-row" className="space-y-1">
          <div className="flex flex-wrap items-center gap-2 text-xs text-ink-3">
            <span>{formatDateTime(r.recordedAt)}</span>
            <Badge>{r.kind === 'visit_note' ? '訪問記録' : r.kind}</Badge>
            {r.source === 'agent' ? <Badge tone="draft">AI</Badge> : null}
          </div>
          <p className="whitespace-pre-wrap text-ink-1">{typeof r.body.text === 'string' ? r.body.text : JSON.stringify(r.body)}</p>
        </Card>
      ))}
    </div>
  );
}

// ---- 帳票 ----

function statusTone(s: DocStatus) {
  return s === 'confirmed' ? 'confirmed' : s === 'draft' || s === 'review' ? 'draft' : 'neutral';
}

function DocBadges({ d }: { d: CareDocument }) {
  return (
    <>
      <Badge tone={statusTone(d.status)}>{DOC_STATUS_LABEL[d.status] ?? d.status}</Badge>
      {d.basisCorrected ? <Badge tone="alert">根拠に訂正あり</Badge> : null}
    </>
  );
}

const formName = (docType: string) => FORMS.get(docType)?.name ?? docType;

function useDocuments(officeId: string, clientId: string) {
  return useLoad(() => api<Items<CareDocument>>(officePath(officeId, 'clients', clientId, 'documents'), { officeId }).then((r) => r.items), [officeId, clientId]);
}

export function DocumentsTab() {
  const { officeId, client } = useClientCtx();
  const { data, error, loading } = useDocuments(officeId, client.id);
  const groups = groupByDocType(data ?? [], CAREMANAGER_FORMS.map((f) => f.docType));
  return (
    <div className="space-y-4">
      <ErrorText>{error}</ErrorText>
      {loading ? <Loading /> : null}
      {data && data.length === 0 ? <Muted>帳票はまだありません。</Muted> : null}
      {groups.map((g) => (
        <section key={g.docType} className="space-y-2">
          <h2 className="text-base font-semibold">{formName(g.docType)}</h2>
          <Card className="divide-y divide-line p-0">
            {g.items.map((d) => (
              <Link key={d.id} to={d.id} data-testid="doc-row" className="flex min-h-11 flex-wrap items-center gap-2 px-4 py-2.5 hover:bg-subtle">
                <span className="font-medium">第{d.version}版</span>
                <DocBadges d={d} />
                <span className="ml-auto text-xs text-ink-4">{formatDateTime(d.confirmedAt ?? d.updatedAt)}</span>
                <ChevronRight size={16} className="text-ink-4" />
              </Link>
            ))}
          </Card>
        </section>
      ))}
    </div>
  );
}

export function DocumentView() {
  const { officeId, client } = useClientCtx();
  const { documentId = '' } = useParams();
  const navigate = useNavigate();
  // 単体取得の API が無いので一覧から探す
  const { data, error, loading, reload } = useDocuments(officeId, client.id);
  const [busy, setBusy] = useState<'review' | 'confirm' | null>(null);
  const [actError, setActError] = useState<string | null>(null);
  const doc = data?.find((d) => d.id === documentId);

  const act = async (kind: 'review' | 'confirm') => {
    setBusy(kind);
    setActError(null);
    try {
      await api(officePath(officeId, 'documents', documentId, kind), { officeId, body: { appCode: doc?.appCode ?? APP_CODE } });
      reload();
    } catch (e) {
      setActError(messageOf(e));
    } finally {
      setBusy(null);
    }
  };

  if (loading && !data) return <Loading />;
  if (error) return <ErrorText>{error}</ErrorText>;
  if (!doc)
    return (
      <Card className="space-y-2">
        <p>帳票が見つかりませんでした。</p>
        <Button variant="secondary" size="sm" onClick={() => navigate('..', { relative: 'path' })}>
          帳票の一覧へ
        </Button>
      </Card>
    );
  return (
    <div className="space-y-4">
      <Link to=".." relative="path" className="inline-flex items-center gap-1 text-sm text-ink-3 hover:text-ink-1">
        <ArrowLeft size={14} />
        帳票の一覧
      </Link>
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="text-lg font-semibold">{formName(doc.docType)}</h2>
        <span className="text-ink-3">第{doc.version}版</span>
        <DocBadges d={doc} />
        {doc.confirmedAt ? <span className="text-xs text-ink-4">確定 {formatDateTime(doc.confirmedAt)}</span> : null}
      </div>
      {doc.basisCorrected ? (
        <p className="rounded-md bg-alert-lt px-3 py-2 text-sm text-alert">この帳票の根拠になった記録があとから訂正されています。内容を見直してください。</p>
      ) : null}
      <Card>
        <DocumentBody docType={doc.docType} body={doc.body} />
      </Card>
      <ErrorText>{actError}</ErrorText>
      {canReview(doc.status) || canConfirm(doc.status) ? (
        <div className="flex flex-wrap justify-end gap-2">
          {canReview(doc.status) ? (
            <Button variant="secondary" data-testid="doc-review" disabled={!!busy} onClick={() => void act('review')}>
              {busy === 'review' ? '送信中…' : '確認へ'}
            </Button>
          ) : null}
          <Button variant="confirm" data-testid="doc-confirm" disabled={!!busy} onClick={() => void act('confirm')}>
            {busy === 'confirm' ? '確定中…' : '確定'}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

// ---- エージェント ----

interface CreateCaseResponse {
  id: string;
  created: boolean;
  agent?: AgentInvokeResult;
}

export function AgentTab() {
  const { officeId, client } = useClientCtx();
  const cases = useLoad(
    () =>
      api<Items<WorkCase>>(officePath(officeId, 'cases'), { officeId }).then((r) =>
        recent(
          r.items.filter((c) => c.clientId === client.id),
          20,
        ),
      ),
    [officeId, client.id],
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<AgentOutcome | null>(null);

  const request = async () => {
    setBusy(true);
    setError(null);
    setOutcome(null);
    try {
      const r = await api<CreateCaseResponse>(officePath(officeId, 'cases'), {
        officeId,
        body: { appCode: APP_CODE, kind: 'draft_support_progress', clientId: client.id, idempotencyKey: `${client.id}:draft_support_progress:${Date.now()}` },
      });
      setOutcome(parseAgentOutcome(r.agent));
      cases.reload();
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setBusy(false);
    }
  };

  const waiting = (cases.data ?? []).filter((c) => c.state === 'waiting');
  const others = (cases.data ?? []).filter((c) => c.state !== 'waiting');

  return (
    <div className="space-y-4">
      <Card className="space-y-3">
        <p className="text-sm text-ink-2">記録をもとに、エージェントが支援経過（第5表）の下書きを作ります。途中で判断を求められたら、ここか「今日やること」で答えてください。</p>
        <Button data-testid="agent-request" onClick={() => void request()} disabled={busy}>
          <Sparkles size={16} />
          {busy ? '依頼中…（少しかかります）' : '支援経過の下書きを頼む'}
        </Button>
        <ErrorText>{error}</ErrorText>
        {outcome ? (
          <p role="status" className="text-sm text-ink-2">
            {outcomeLabel(outcome)}
          </p>
        ) : null}
        {outcome?.status === 'done' && outcome.text ? <p className="whitespace-pre-wrap rounded-md bg-subtle p-3 text-sm">{outcome.text}</p> : null}
      </Card>

      <ErrorText>{cases.error}</ErrorText>
      {cases.loading && !cases.data ? <Loading /> : null}
      {waiting.map((c) => (
        <Card key={`${c.id}:${c.generation}`} className="space-y-3">
          <div className="flex items-center gap-2 text-sm">
            <span className="font-medium">{CASE_KIND_LABEL[c.kind] ?? c.kind}</span>
            <Badge tone="alert">判断待ち</Badge>
          </div>
          <CaseAnswerForm officeId={officeId} caseId={c.id} generation={c.generation} question={questionText(c)} />
        </Card>
      ))}
      {others.length > 0 ? (
        <section className="space-y-2">
          <h2 className="text-base font-semibold">この利用者の案件</h2>
          <Card className="divide-y divide-line p-0">
            {others.map((c) => (
              <div key={c.id} className="flex flex-wrap items-center gap-2 px-4 py-2.5 text-sm">
                <span>{CASE_KIND_LABEL[c.kind] ?? c.kind}</span>
                <Badge tone={c.state === 'done' ? 'confirmed' : 'neutral'}>{CASE_STATE_LABEL[c.state] ?? c.state}</Badge>
                <span className="ml-auto text-xs text-ink-4">{formatDateTime(c.updatedAt)}</span>
              </div>
            ))}
          </Card>
        </section>
      ) : null}
    </div>
  );
}
